/**
 * User Provisioning Hooks
 * ISO 27001 A.9.2.1 - User registration and de-registration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  userProvisioningApi,
  ProvisioningRequestInput,
  ProvisioningStatus,
} from '../api/user-provisioning.api';

// Query keys
const PROVISIONING_REQUESTS_KEY = ['user-provisioning', 'requests'];
const PROVISIONING_PENDING_KEY = ['user-provisioning', 'pending'];
const PROVISIONING_MY_REQUESTS_KEY = ['user-provisioning', 'my-requests'];
const PROVISIONING_STATS_KEY = ['user-provisioning', 'stats'];

/**
 * Hook to get all provisioning requests
 */
export function useProvisioningRequests(options?: {
  status?: ProvisioningStatus;
  requesterId?: string;
}) {
  return useQuery({
    queryKey: [...PROVISIONING_REQUESTS_KEY, options],
    queryFn: () => userProvisioningApi.getAllRequests(options),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get pending requests for approval
 */
export function usePendingProvisioningRequests() {
  return useQuery({
    queryKey: PROVISIONING_PENDING_KEY,
    queryFn: () => userProvisioningApi.getPendingRequests(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook to get my requests
 */
export function useMyProvisioningRequests() {
  return useQuery({
    queryKey: PROVISIONING_MY_REQUESTS_KEY,
    queryFn: () => userProvisioningApi.getMyRequests(),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get a specific request
 */
export function useProvisioningRequest(id: string) {
  return useQuery({
    queryKey: [...PROVISIONING_REQUESTS_KEY, id],
    queryFn: () => userProvisioningApi.getRequest(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get provisioning statistics
 */
export function useProvisioningStats() {
  return useQuery({
    queryKey: PROVISIONING_STATS_KEY,
    queryFn: () => userProvisioningApi.getStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a provisioning request
 */
export function useCreateProvisioningRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProvisioningRequestInput) =>
      userProvisioningApi.createRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROVISIONING_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_MY_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_STATS_KEY });
    },
  });
}

/**
 * Hook to approve a request
 */
export function useApproveProvisioningRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      userProvisioningApi.approveRequest(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROVISIONING_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_PENDING_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_STATS_KEY });
    },
  });
}

/**
 * Hook to reject a request
 */
export function useRejectProvisioningRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      userProvisioningApi.rejectRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROVISIONING_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_PENDING_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_STATS_KEY });
    },
  });
}

/**
 * Hook to cancel a request
 */
export function useCancelProvisioningRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userProvisioningApi.cancelRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROVISIONING_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_MY_REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: PROVISIONING_STATS_KEY });
    },
  });
}
